import { useMutation, useQuery } from '@apollo/client';
import { PillItem, Pills, SelectChangeEvent, TableActions, Tabs, useFlashMessage } from '@indigo-ag/component-module';
import { AGFCustomFilters, AGFTotals, AgFinancing, AgFinancingConnection } from '@indigo-ag/schema';
import i18next from 'core/i18n';
import debounce from 'lodash/debounce';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { NewPage } from 'core/components';
import { UserContext } from 'core/components/GateWay/GateWay';
import {
  AgFinanceQueueCustomActions,
  FinancingProcessFilterCriteria,
  LoadingWrapper,
  Permissions,
  SortType,
  TABLE_ACTIONS,
  TableSort,
  TextSearchInput,
  UpdateCreditAnalystPayload,
  createStylesHook,
  defaultPagination,
  utils
} from 'shared';
import { defaultSortingBehavior, heapUtil, localStorageUtils, text } from 'shared/utils';
import { CreditAnalystSelector } from '../../../shared/components/CreditAnalystSelector/CreditAnalystSelector';
import { AgFinancingQueue, ColumnId } from '../components/AgFinancingQueue';
import { getAgFinancingQueueQuery, getAgFinancingTotalsQuery, updateCreditAnalystAssigned } from '../queries';
import {
  AGFCreditAnalystAssignedFilterKeys,
  CREDIT_ANALYST_DEFAULT_VALUES,
  FormattedAgFinanceRecord,
  formatAgFinanceQueue
} from '../shared-br-grower';
import { HiddenTabIds, PILLS_CONFIG, PillIds, TABS_CONFIG, TabIds } from './tabs/config';

interface IInitialState {
  initialPage: () => number;
  initialRows: () => number;
  initialSearch: () => string;
  initialTab: () => TabIds | null;
  initialPill: () => PillIds | null;
  initialSort: TableSort | null;
}

export const defaultSort: TableSort = {
  id: 'name',
  direction: SortType.ASC
};

/**
 * This is a nasty hack, kirby-team is working on supporting
 * parent pill custom styling.
 */
const useStyles = createStylesHook({
  pillParent: {
    '& div[aria-label="AgFinancing Queue Filter Pills"]': {
      flexWrap: 'wrap',
      '& button': {
        marginBottom: 10
      }
    }
  }
});

const CreditAnalystSelectorWrapper = styled.div`
  padding: 0 18px 6px 0;
`;

export const BrazilGrowersPage: React.FunctionComponent = () => {
  /**
   * User Context
   */
  const userContext = useContext(UserContext);
  const isInternal =
    userContext.hasPermission(Permissions.FA_INTERNAL) ||
    userContext.isSuper ||
    userContext.hasPermission(Permissions.FA_ADMIN);
  const isExternal = userContext.hasPermission(Permissions.FA_EXTERNAL);
  const isCreditLead = userContext.hasPermission(Permissions.FA_CREDIT_LEAD);
  const isFlc = userContext.hasPermission(Permissions.FA_EXTERNAL_CREDIT);
  const isLuchesi = userContext.hasPermission(Permissions.FA_EXTERNAL_LEGAL);
  const isTerramagna = userContext.hasPermission(Permissions.FA_EXTERNAL_FIELD_MONITORING);
  const isSecretary = userContext.hasPermission(Permissions.FA_SECRETARY);
  const isDirector = userContext.isFinanceDirector || userContext.isCommercialDirector;
  const isInternalNotLead = isInternal && !(isCreditLead || isDirector);

  const tabConfig = isLuchesi
    ? TABS_CONFIG.externalLegal
    : isExternal
    ? TABS_CONFIG.external
    : isCreditLead || isDirector
    ? TABS_CONFIG.creditLead
    : isSecretary
    ? TABS_CONFIG.secretary
    : TABS_CONFIG.internal;

  /**
   * State
   */
  const initialState: IInitialState = {
    initialPage: () => defaultPagination.currentPage(FinancingProcessFilterCriteria.CURRENT_PAGE),
    initialRows: () => defaultPagination.rowsPerPage,
    initialSearch: () => localStorageUtils.get(FinancingProcessFilterCriteria.SEARCH_TEXT) || '', // ! this is broken
    initialTab: () =>
      (localStorageUtils.get(FinancingProcessFilterCriteria.ACTIVE_TAB) as TabIds) ||
      (isInternalNotLead || isLuchesi ? tabConfig[0].id : null),
    initialPill: () =>
      (localStorageUtils.get(FinancingProcessFilterCriteria.ACTIVE_PILL) as PillIds) ||
      (pillsConfig?.length ? pillsConfig[0].id : null),
    initialSort: null
  };

  /**
   * Styles
   */
  const classes = useStyles();

  const [activeTab, setActiveTab] = useState<TabIds | null>(initialState.initialTab);
  const [pillsConfig, setPillsConfig] = useState(
    (isInternalNotLead || isLuchesi) && activeTab
      ? PILLS_CONFIG[activeTab]
      : isFlc
      ? PILLS_CONFIG[HiddenTabIds.EXTERNAL_CREDIT]
      : isTerramagna
      ? PILLS_CONFIG[HiddenTabIds.TERRAMAGNA]
      : PILLS_CONFIG[HiddenTabIds.LEAD]
  );
  const [activePill, setActivePill] = useState<PillIds | null>(initialState.initialPill);
  const [currentPage, setCurrentPage] = useState(initialState.initialPage);
  const [rowsPerPage] = useState(initialState.initialRows);
  const [searchText, setSearchText] = useState('');
  const [searchTextQuery, setSearchTextQuery] = useState('');
  const [sort, setSort] = useState(initialState.initialSort);
  const [analystSearchValue, setAnalystSearchValue] = useState<AGFCustomFilters['creditAnalystAssigned']>();
  const [preSelectedAnalystId, setPreSelectedAnalystId] = useState<string>('');

  /**
   * Mutations
   */
  const [updateCreditAnalystAssignedFn] = useMutation(updateCreditAnalystAssigned);

  useEffect(() => {
    localStorageUtils.set(FinancingProcessFilterCriteria.CURRENT_PAGE, currentPage);
    if (activeTab) {
      localStorageUtils.set(FinancingProcessFilterCriteria.ACTIVE_TAB, activeTab);
    } else {
      localStorageUtils.remove(FinancingProcessFilterCriteria.ACTIVE_TAB);
    }

    if (activePill) {
      localStorageUtils.set(FinancingProcessFilterCriteria.ACTIVE_PILL, activePill);
    } else {
      localStorageUtils.remove(FinancingProcessFilterCriteria.ACTIVE_PILL);
    }
  }, [currentPage, rowsPerPage, activeTab, activePill]);

  const updateSelectedTab = (newTab: TabIds) => {
    setActiveTab(newTab);
    const newPillsConfig = PILLS_CONFIG[newTab] || [];
    setPillsConfig(newPillsConfig);
    setActivePill(newPillsConfig.length ? newPillsConfig[0].id : null);
    setCurrentPage(0);
  };

  const updateSelectedPill = (newPill: PillIds): void => {
    setActivePill(newPill);
    setCurrentPage(0);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setSearchText(event.target.value);
    setSearchCriteria(event.target.value);
  };

  const handleCancelSearch = () => {
    setCurrentPage(0);
    setSearchText('');
    setSearchTextQuery('');
  };

  const handleCreditAnalystChange = (event: SelectChangeEvent) => {
    const selection = event.target.value;

    setPreSelectedAnalystId(selection || '');
    setAnalystSearchValue(
      selection
        ? AGFCreditAnalystAssignedFilterKeys.has(selection as CREDIT_ANALYST_DEFAULT_VALUES)
          ? { [selection]: true }
          : { creditAnalystAssignedId: selection }
        : undefined
    );
  };

  const setSearchCriteria = useCallback(
    debounce((criteria: string) => {
      // Reset the page when you query because it's another different result
      setCurrentPage(0);
      setSearchTextQuery(criteria);

      // ? should we use redux to track heap custom events?.
      // ? I think in the future would be difficult to get different information like,
      // ? user email, user role, with country the user is, how many queries have been perform, etc.
      // ? In this case the component will require a lot of information that is not related to its work
      // ? and eventually will overwhelm the component with context and params to be able to get that information
      // ? making heap tracking difficult to escalate
      heapUtil.track('global-search-working-queue', { query: criteria });
    }, 400),
    []
  );

  const renderAgFinanceQueue = ({ data }: { data: { financingAgFinancingQueue: AgFinancingConnection } }) => {
    const allNodes: AgFinancing[] = data?.financingAgFinancingQueue?.edges.map(e => e.node);
    const pagination = {
      limit: rowsPerPage,
      offset: currentPage * rowsPerPage,
      total: data?.financingAgFinancingQueue?.totalCount || 0
    };

    const handleTableAction = (action: TableActions<ColumnId> | AgFinanceQueueCustomActions) => {
      if (action.type === TABLE_ACTIONS.NEXT_PAGE) {
        return setCurrentPage(currentPage + 1);
      }

      if (action.type === TABLE_ACTIONS.PREVIOUS_PAGE) {
        return setCurrentPage(currentPage - 1);
      }

      if (action.type === TABLE_ACTIONS.SORT_CHANGED && action.payload) {
        setCurrentPage(0);
        if (
          sort?.id === action.payload.id &&
          sort?.direction === SortType.ASC &&
          action.payload.direction === SortType.ASC
        ) {
          return setSort(null);
        }

        return defaultSortingBehavior(action.payload, setSort, sort);
      }

      if (action.type === TABLE_ACTIONS.UPDATE_CREDIT_ANALYST && action.payload) {
        return updateCreditAnalyst(action.payload);
      }
    };

    const formattedAgFinanceQueue: FormattedAgFinanceRecord[] = formatAgFinanceQueue(allNodes || []);

    return (
      <AgFinancingQueue
        queue={formattedAgFinanceQueue}
        pagination={pagination}
        onTableAction={handleTableAction}
        activeTab={activeTab}
        sort={sort || defaultSort}
        searching={!!searchText}
        activePill={activePill}
      />
    );
  };

  const getGrowersCustomFilters = (pills: PillIds[] = []): AGFCustomFilters => {
    // Avoiding unnecessary custom filters in this scenario to preserve the original behavior
    // since the search input and the custom filters are mutually exclusive.
    if (!!(analystSearchValue && searchTextQuery)) return { creditAnalystAssigned: analystSearchValue };

    const cFilters: AGFCustomFilters = {
      backgroundCheck:
        isInternal && !isCreditLead && activeTab === TabIds.BACKGROUND_CHECK
          ? {
              pending: pills.includes(PillIds.BACKGROUND_CHECK_PENDING),
              sent: pills.includes(PillIds.BACKGROUND_CHECK_SENT),
              waitingCreditManagerApproval: pills.includes(PillIds.BACKGROUND_CHECK_MANAGERIAL_APPROVAL)
            }
          : undefined,
      creditAnalysis:
        isInternal && !isCreditLead && activeTab === TabIds.CREDIT_ANALYSIS
          ? {
              inProgress: pills.includes(PillIds.CREDIT_ANALYSIS_IN_PROGRESS),
              awaitingApproval: pills.includes(PillIds.CREDIT_ANALYSIS_AWAITING_APPROVAL),
              withCommercial: pills.includes(PillIds.CREDIT_ANALYSIS_WITH_COMMERCIAL),
              approvedCredits: pills.includes(PillIds.CREDIT_ANALYSIS_APPROVED_CREDIT),
              rejectedCredits: pills.includes(PillIds.CREDIT_ANALYSIS_REJECTED_CREDIT)
            }
          : undefined,
      collateralAnalysis:
        isInternal && !isCreditLead && activeTab === TabIds.COLLATERAL_ANALYSIS
          ? {
              inProgress: pills.includes(PillIds.COLLATERAL_ANALYSIS_IN_PROGRESS),
              awaitingApproval: pills.includes(PillIds.COLLATERAL_ANALYSIS_AWAITING_APPROVAL),
              withCommercial: pills.includes(PillIds.COLLATERAL_ANALYSIS_WITH_COMMERCIAL),
              approvedCollaterals: pills.includes(PillIds.COLLATERAL_ANALYSIS_PHASE_APPROVED),
              rejectedCollaterals: pills.includes(PillIds.COLLATERAL_ANALYSIS_PHASE_REJECTED)
            }
          : undefined,
      documentRegistration:
        isInternal &&
        !isCreditLead &&
        (activeTab === TabIds.DOCUMENT_REGISTRATION || activeTab === TabIds.DOCUMENT_REGISTRATION_SECRETARY)
          ? {
              activationPending: pills.includes(PillIds.P3_ACTIVATION_PENDING),
              waitingDraft: pills.includes(PillIds.GENERATE_DRAFT),
              waitingAmendment: pills.includes(PillIds.AMENDMENT_CREATION),
              draftEmitted: pills.includes(PillIds.REGISTERING),
              reviewingDocuments: pills.includes(PillIds.DOCUMENTS_VALIDATION),
              creditAnalystApproval: pills.includes(PillIds.DOCUMENTS_VALIDATION_CA),
              waitingCreditManagerApproval: pills.includes(PillIds.DOCUMENTS_VALIDATION_CM),
              withCommercial: pills.includes(PillIds.CREDIT_ANALYSIS_WITH_COMMERCIAL),
              pendingContractValidation: pills.includes(PillIds.INVESTMENT_FUND_ASSIGNMENT_PENDING_CONTRACT)
            }
          : undefined,
      investmentFundAssignment:
        isInternal && !isCreditLead && activeTab === TabIds.INVESTMENT_FUND_ASSIGNMENT
          ? {
              pendingContractValidation: pills.includes(PillIds.INVESTMENT_FUND_ASSIGNMENT_PENDING_CONTRACT),
              awaitingAssignment: pills.includes(PillIds.INVESTMENT_FUND_ASSIGNMENT_AWAITING),
              withCommercial: pills.includes(PillIds.INVESTMENT_FUND_ASSIGNMENT_WITH_COMMERCIAL),
              submittedMinuta: pills.includes(PillIds.INVESTMENT_FUND_ASSIGNMENT_SUBMITTED_MINUTA)
            }
          : undefined,
      externalCredit:
        isExternal && isFlc
          ? {
              newNegotiation: pills.includes(PillIds.EXTERNAL_CREDIT_NEW_NEGOTIATION),
              inAnalysis: pills.includes(PillIds.EXTERNAL_CREDIT_IN_ANALYSIS),
              onHold: pills.includes(PillIds.ON_HOLD),
              analysisSent: pills.includes(PillIds.EXTERNAL_CREDIT_ANALYSIS_SENT)
            }
          : undefined,
      externalLegal:
        isExternal && isLuchesi
          ? {
              newNegotiation: pills.includes(PillIds.EXTERNAL_LEGAL_NEW_NEGOTIATION),
              inAnalysis: pills.includes(PillIds.EXTERNAL_LEGAL_IN_ANALYSIS),
              generateDraft: pills.includes(PillIds.GENERATE_DRAFT),
              waitingAmendment: pills.includes(PillIds.AMENDMENT_CREATION),
              waitingDigitalSignatures: pills.includes(PillIds.AMENDMENT_SIGN_DIGITALLY),
              waitingDocuments: pills.includes(PillIds.REGISTERING),
              reviewingDocuments: pills.includes(PillIds.DOCUMENTS_VALIDATION),
              documentsValidated: pills.includes(PillIds.AWAITING_PHYSICAL_DOCUMENTS),
              onHold: pills.includes(PillIds.ON_HOLD)
            }
          : undefined,
      creditLead:
        isCreditLead || isDirector
          ? {
              requiresLeadApprovalBackgroundCheck: pills.includes(PillIds.LEAD_IN_BACKGROUND_CHECK),
              requiresLeadApproval: pills.includes(PillIds.LEAD_IN_ANALYSIS),
              pendingDocumentsApproval: pills.includes(PillIds.LEAD_IN_FORMALIZATION)
            }
          : undefined,
      fieldMonitor: isTerramagna
        ? {
            newNegotiation: pills.includes(PillIds.TERRAMAGNA_NEW_CONTRACTS),
            inProgress: pills.includes(PillIds.TERRAMAGNA_IN_PROGRESS),
            withCommercial: pills.includes(PillIds.CREDIT_ANALYSIS_WITH_COMMERCIAL),
            completed: pills.includes(PillIds.TERRAMAGNA_COMPLETED)
          }
        : undefined,
      creditAnalystAssigned: analystSearchValue
    };

    return cFilters;
  };

  const financingTotalsInfo = getAgFinancingTotalsQuery(
    getGrowersCustomFilters((pillsConfig || []).map((pill: { id: PillIds }) => pill.id))
  );
  const { data: totals } = useQuery(financingTotalsInfo.query, {
    variables: financingTotalsInfo.variables,
    fetchPolicy: 'cache-first'
  });

  const mapTotalsToPills = (totals: AGFTotals = {}, config: PillItem<PillIds>[]) => {
    return config.map(pill => {
      switch (pill.id) {
        case PillIds.BACKGROUND_CHECK_PENDING:
          return { ...pill, count: totals.backgroundCheck?.pending };
        case PillIds.BACKGROUND_CHECK_SENT:
          return { ...pill, count: totals.backgroundCheck?.sent };
        case PillIds.BACKGROUND_CHECK_MANAGERIAL_APPROVAL:
          return { ...pill, count: totals.backgroundCheck?.waitingCreditManagerApproval };
        case PillIds.CREDIT_ANALYSIS_IN_PROGRESS:
          return { ...pill, count: totals.creditAnalysis?.inProgress };
        case PillIds.CREDIT_ANALYSIS_AWAITING_APPROVAL:
          return { ...pill, count: totals.creditAnalysis?.awaitingApproval };
        case PillIds.CREDIT_ANALYSIS_WITH_COMMERCIAL:
          return {
            ...pill,
            count:
              activeTab === TabIds.CREDIT_ANALYSIS
                ? totals.creditAnalysis?.withCommercial
                : totals.documentRegistration?.withCommercial ?? totals.fieldMonitor?.withCommercial
          };
        case PillIds.CREDIT_ANALYSIS_APPROVED_CREDIT:
          return { ...pill, count: totals.creditAnalysis?.approvedCredits };
        case PillIds.CREDIT_ANALYSIS_REJECTED_CREDIT:
          return { ...pill, count: totals.creditAnalysis?.rejectedCredits };
        case PillIds.COLLATERAL_ANALYSIS_IN_PROGRESS:
          return { ...pill, count: totals.collateralAnalysis?.inProgress };
        case PillIds.COLLATERAL_ANALYSIS_AWAITING_APPROVAL:
          return { ...pill, count: totals.collateralAnalysis?.awaitingApproval };
        case PillIds.COLLATERAL_ANALYSIS_WITH_COMMERCIAL:
          return { ...pill, count: totals.collateralAnalysis?.withCommercial };
        case PillIds.COLLATERAL_ANALYSIS_PHASE_APPROVED:
          return { ...pill, count: totals.collateralAnalysis?.approvedCollaterals };
        case PillIds.COLLATERAL_ANALYSIS_PHASE_REJECTED:
          return { ...pill, count: totals.collateralAnalysis?.rejectedCollaterals };
        case PillIds.P3_ACTIVATION_PENDING:
          return { ...pill, count: totals.documentRegistration?.activationPending };
        case PillIds.GENERATE_DRAFT:
          return {
            ...pill,
            count:
              activeTab === TabIds.DOCUMENT_REGISTRATION
                ? totals.documentRegistration?.waitingDraft
                : totals.externalLegal?.generateDraft
          };
        case PillIds.AMENDMENT_CREATION:
          return {
            ...pill,
            count:
              activeTab === TabIds.DOCUMENT_REGISTRATION
                ? totals.documentRegistration?.waitingAmendment
                : totals.externalLegal?.waitingAmendment
          };
        case PillIds.AMENDMENT_SIGN_DIGITALLY:
          return {
            ...pill,
            count:
              activeTab === TabIds.DOCUMENT_REGISTRATION
                ? totals.documentRegistration?.waitingDigitalSignatures
                : totals.externalLegal?.waitingDigitalSignatures
          };
        case PillIds.AMENDMENT_SIGN_PHYSICALLY:
          return { ...pill, count: totals.documentRegistration?.waitingPhysicalSignature };
        case PillIds.REGISTERING:
          return {
            ...pill,
            count:
              activeTab === TabIds.DOCUMENT_REGISTRATION
                ? totals.documentRegistration?.draftEmitted
                : totals.externalLegal?.waitingDocuments
          };
        case PillIds.DOCUMENTS_VALIDATION:
          return {
            ...pill,
            count:
              activeTab === TabIds.DOCUMENT_REGISTRATION
                ? totals.documentRegistration?.reviewingDocuments
                : totals.externalLegal?.reviewingDocuments
          };
        case PillIds.DOCUMENTS_VALIDATION_CA:
          return { ...pill, count: totals.documentRegistration?.creditAnalystApproval };
        case PillIds.DOCUMENTS_VALIDATION_CM:
          return { ...pill, count: totals.documentRegistration?.waitingCreditManagerApproval };

        case PillIds.INVESTMENT_FUND_ASSIGNMENT_PENDING_CONTRACT:
          return { ...pill, count: totals.documentRegistration?.pendingContractValidation };

        case PillIds.EXTERNAL_CREDIT_NEW_NEGOTIATION:
          return { ...pill, count: totals.externalCredit?.newNegotiation };
        case PillIds.EXTERNAL_CREDIT_IN_ANALYSIS:
          return { ...pill, count: totals.externalCredit?.inAnalysis };
        case PillIds.ON_HOLD:
          return { ...pill, count: totals.externalCredit?.onHold ?? totals.externalLegal?.onHold };
        case PillIds.EXTERNAL_CREDIT_ANALYSIS_SENT:
          return { ...pill, count: totals.externalCredit?.analysisSent };

        case PillIds.EXTERNAL_LEGAL_NEW_NEGOTIATION:
          return { ...pill, count: totals.externalLegal?.newNegotiation };
        case PillIds.EXTERNAL_LEGAL_IN_ANALYSIS:
          return { ...pill, count: totals.externalLegal?.inAnalysis };
        case PillIds.AWAITING_PHYSICAL_DOCUMENTS:
          return { ...pill, count: totals.externalLegal?.documentsValidated };

        case PillIds.LEAD_IN_BACKGROUND_CHECK:
          return { ...pill, count: totals.creditLead?.requiresLeadApprovalBackgroundCheck };
        case PillIds.LEAD_IN_ANALYSIS:
          return { ...pill, count: totals.creditLead?.requiresLeadApproval };
        case PillIds.LEAD_IN_FORMALIZATION:
          return { ...pill, count: totals.creditLead?.pendingDocumentsApproval };

        case PillIds.TERRAMAGNA_NEW_CONTRACTS:
          return { ...pill, count: totals.fieldMonitor?.newNegotiation };
        case PillIds.TERRAMAGNA_IN_PROGRESS:
          return { ...pill, count: totals.fieldMonitor?.inProgress };
        case PillIds.TERRAMAGNA_COMPLETED:
          return { ...pill, count: totals.fieldMonitor?.completed };

        default:
          return { ...pill, count: 0 };
      }
    });
  };

  const financingQueueInfo = getAgFinancingQueueQuery(
    rowsPerPage,
    currentPage,
    searchTextQuery,
    getGrowersCustomFilters(activePill ? [activePill] : undefined),
    sort,
    analystSearchValue
  );

  const { addFlashMessage } = useFlashMessage();
  const updateCreditAnalyst = ({ agFinancingId, userId }: UpdateCreditAnalystPayload) => {
    if (userId === null) return; // Returns if selecting the placeholder
    const id = AGFCreditAnalystAssignedFilterKeys.has(userId as CREDIT_ANALYST_DEFAULT_VALUES) ? null : userId;
    updateCreditAnalystAssignedFn({
      variables: {
        agFinancingId: agFinancingId,
        userId: id
      },
      awaitRefetchQueries: true,
      refetchQueries: [financingQueueInfo, financingTotalsInfo]
    })
      .then(() => {
        addFlashMessage({
          content: text.firstLetterUppercase(i18next.t('creditAnalystAssigned')),
          title: '',
          variant: 'success'
        });
      })
      .catch(() => {
        addFlashMessage({
          content: text.firstLetterUppercase(i18next.t('creditAnalystNotAssigned')),
          title: '',
          variant: 'error'
        });
      });
  };

  return (
    <NewPage
      title={!searchText ? i18next.t('contracts') : i18next.t('resultPage')}
      subtitle={isCreditLead || isDirector ? i18next.t('requiresApproval') : undefined}
      contentRight={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <CreditAnalystSelectorWrapper>
            <CreditAnalystSelector
              initialOptions={[
                {
                  name: utils.text.firstLetterUppercase(i18next.t('unassigned')),
                  value: CREDIT_ANALYST_DEFAULT_VALUES.UNASSIGNED
                },
                {
                  name: utils.text.firstLetterUppercase(i18next.t('assigned')),
                  value: CREDIT_ANALYST_DEFAULT_VALUES.ASSIGNED
                }
              ]}
              label={utils.text.toTitleCase(i18next.t('creditAnalyst'))}
              onChange={handleCreditAnalystChange}
              preSelectedAnalystId={preSelectedAnalystId}
              placeholder={utils.text.toTitleCase(i18next.t('allAnalysts'))}
            />
          </CreditAnalystSelectorWrapper>
          <TextSearchInput
            data-test="global-search-agfinance-working-queue"
            searchText={searchText}
            handleSearch={handleSearch}
            handleCancelSearch={handleCancelSearch}
          />
        </div>
      }
    >
      <>
        {!searchText && (
          <>
            {tabConfig.length && activeTab ? (
              <Tabs
                activeTabId={activeTab}
                ariaLabel="AgFinancing Queue Filter Tabs"
                items={tabConfig}
                onChange={updateSelectedTab}
                className="mb32"
              />
            ) : null}
            <div className="flex mb32">
              {pillsConfig?.length && activePill ? (
                <div className="flex flex-space-btw width-100">
                  <div className={classes.pillParent}>
                    <Pills
                      activePillId={activePill}
                      ariaLabel="AgFinancing Queue Filter Pills"
                      items={mapTotalsToPills(totals?.AgFinancingTotals, pillsConfig)}
                      onChange={updateSelectedPill}
                    />
                  </div>
                  {activeTab === TabIds.INVESTMENT_FUND_ASSIGNMENT ? (
                    <Link to="/cessions">{text.firstLetterUppercase(i18next.t('seeCessionsGroups'))}</Link>
                  ) : null}
                </div>
              ) : null}
            </div>
          </>
        )}
        <LoadingWrapper
          query={financingQueueInfo.query}
          queryOptions={{
            variables: financingQueueInfo.variables,
            fetchPolicy: 'cache-and-network'
          }}
        >
          {renderAgFinanceQueue}
        </LoadingWrapper>
      </>
    </NewPage>
  );
};
